"use client";

// Stashes the home-page prompt + selected template in localStorage as a
// fallback for the URL-passthrough path. The happy path is `?next=`
// piggybacking through the login modal — but if the user dismisses the
// modal, hits back, or otherwise drops off that path, the prompt is lost
// from the URL. This stash lets us recover after they sign in via any
// other route.

const KEY = "panda:home-prompt-resume";
const TTL_MS = 60 * 60 * 1000;

type Stash = {
  prompt: string;
  templateId?: string;
  // Durable project id for the logged-out → signed-in handoff. Minted ONCE when
  // the logged-out user submits, so every post-auth resume path (mount
  // urlPrompt, mount stash, SIGNED_IN) reuses the SAME id and converges on one
  // /project/<id> — instead of each minting a fresh uuid, which created
  // duplicate projects and double-charged credits after signup + email
  // verification (QA dup-build bug).
  projectId?: string;
  timestamp: number;
};

export function stashHomePrompt(
  prompt: string,
  templateId?: string,
  projectId?: string,
) {
  if (typeof window === "undefined") return;
  const trimmed = prompt.trim();
  if (!trimmed) {
    return;
  }
  try {
    const stash: Stash = {
      prompt: trimmed,
      templateId,
      projectId,
      timestamp: Date.now(),
    };
    localStorage.setItem(KEY, JSON.stringify(stash));
  } catch (err) {
    // localStorage can throw in private browsing / quota-exceeded.
    // The URL-passthrough path still works; this is belt-and-suspenders.
    console.warn("[stash] save failed:", err);
  }
}

export function readHomePromptStash():
  | { prompt: string; templateId?: string; projectId?: string }
  | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<Stash>;
    if (
      typeof parsed?.prompt !== "string" ||
      !parsed.prompt.trim() ||
      typeof parsed.timestamp !== "number" ||
      Date.now() - parsed.timestamp > TTL_MS
    ) {
      localStorage.removeItem(KEY);
      return null;
    }
    return {
      prompt: parsed.prompt,
      templateId:
        typeof parsed.templateId === "string" ? parsed.templateId : undefined,
      projectId:
        typeof parsed.projectId === "string" ? parsed.projectId : undefined,
    };
  } catch (err) {
    console.warn("[stash] read failed:", err);
    try {
      localStorage.removeItem(KEY);
    } catch {
      // ignore
    }
    return null;
  }
}

export function clearHomePromptStash() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(KEY);
    console.log("[stash] cleared");
  } catch (err) {
    console.warn("[stash] clear failed:", err);
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Un-submitted draft stash.
//
// The stash above only exists once the user *submits* — it's the auth-handoff
// carrier. This one covers the step before that: a logged-out visitor types an
// idea, then refreshes, navigates to /templates, or closes the tab without ever
// hitting enter. Without this the box comes back empty and they have to retype
// it from memory. Written debounced while they type (logged-out only), restored
// on mount, cleared on submit and on sign-out.
//
// Separate key + shorter TTL than the resume stash on purpose: a draft is never
// consumed to *start* a build, so it must never be mistaken for one.
// ───────────────────────────────────────────────────────────────────────────

const DRAFT_KEY = "panda:home-prompt-draft";
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
// A prompt this long is already past anything the composer expects; cap it so a
// runaway paste can't blow the localStorage quota for the rest of the origin.
const DRAFT_MAX_CHARS = 10_000;

type DraftStash = {
  prompt: string;
  timestamp: number;
};

export function saveHomePromptDraft(prompt: string) {
  if (typeof window === "undefined") return;
  // Keep the raw text (not trimmed) so the caret/trailing space the user left
  // behind survives the round-trip; only the emptiness check is trim-based.
  if (!prompt.trim()) {
    clearHomePromptDraft();
    return;
  }
  try {
    const draft: DraftStash = {
      prompt: prompt.slice(0, DRAFT_MAX_CHARS),
      timestamp: Date.now(),
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch (err) {
    // Private browsing / quota. Losing the draft is not worth breaking typing.
    console.warn("[stash] draft save failed:", err);
  }
}

export function readHomePromptDraft(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DraftStash>;
    if (
      typeof parsed?.prompt !== "string" ||
      !parsed.prompt.trim() ||
      typeof parsed.timestamp !== "number" ||
      Date.now() - parsed.timestamp > DRAFT_TTL_MS
    ) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return parsed.prompt.slice(0, DRAFT_MAX_CHARS);
  } catch (err) {
    console.warn("[stash] draft read failed:", err);
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {
      // ignore
    }
    return null;
  }
}

export function clearHomePromptDraft() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch (err) {
    console.warn("[stash] draft clear failed:", err);
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Business-URL intent stash.
//
// When a logged-out user submits a business/website URL on the home hero, the
// URL rides through auth via `?mode=business&url=` on the login modal's `next`.
// This stash is the belt-and-suspenders fallback (modal dismissed, back button,
// session bounce) so the URL survives and we can re-show the Build/clone-vs-Grow
// choice after they sign in. Mirrors the prompt stash above.
// ───────────────────────────────────────────────────────────────────────────

const BIZ_KEY = "panda:home-business-url-resume";

type BusinessStash = {
  url: string;
  timestamp: number;
};

export function stashBusinessIntent(url: string) {
  if (typeof window === "undefined") return;
  const trimmed = url.trim();
  if (!trimmed) return;
  try {
    const stash: BusinessStash = { url: trimmed, timestamp: Date.now() };
    localStorage.setItem(BIZ_KEY, JSON.stringify(stash));
  } catch (err) {
    console.warn("[stash] business save failed:", err);
  }
}

export function readBusinessIntent(): { url: string } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(BIZ_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BusinessStash>;
    if (
      typeof parsed?.url !== "string" ||
      !parsed.url.trim() ||
      typeof parsed.timestamp !== "number" ||
      Date.now() - parsed.timestamp > TTL_MS
    ) {
      localStorage.removeItem(BIZ_KEY);
      return null;
    }
    return { url: parsed.url };
  } catch (err) {
    console.warn("[stash] business read failed:", err);
    try {
      localStorage.removeItem(BIZ_KEY);
    } catch {
      // ignore
    }
    return null;
  }
}

export function clearBusinessIntent() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(BIZ_KEY);
  } catch (err) {
    console.warn("[stash] business clear failed:", err);
  }
}
